import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, FileText, Building2, Layers, BookOpen, Users, HelpCircle } from 'lucide-react';
import { searchData, searchCategories, SearchItem, SearchCategory } from '@/data/searchData';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const categoryIcons: Record<string, React.ElementType> = {
  Solutions: Building2,
  Industries: Layers,
  Platform: FileText,
  Resources: BookOpen,
  Company: Users,
  Support: HelpCircle,
};

const categoryColors: Record<string, string> = {
  Solutions: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  Industries: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400',
  Platform: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
  Resources: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
  Company: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-400',
  Support: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
};

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<SearchCategory>('All');
  const [results, setResults] = useState<SearchItem[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Search function
  const performSearch = useCallback((searchQuery: string, category: SearchCategory) => {
    if (!searchQuery.trim()) {
      // Show popular/recent items when no query
      const popularItems = searchData.slice(0, 6);
      return category === 'All' 
        ? popularItems 
        : popularItems.filter(item => item.category === category);
    }

    const lowerQuery = searchQuery.toLowerCase();
    
    let filtered = searchData.filter(item => {
      const matchesTitle = item.title.toLowerCase().includes(lowerQuery);
      const matchesDescription = item.description.toLowerCase().includes(lowerQuery);
      const matchesKeywords = item.keywords.some(kw => kw.toLowerCase().includes(lowerQuery));
      const matchesType = item.type.toLowerCase().includes(lowerQuery);
      
      return matchesTitle || matchesDescription || matchesKeywords || matchesType;
    });

    if (category !== 'All') {
      filtered = filtered.filter(item => item.category === category);
    }

    // Sort by relevance (title matches first)
    filtered.sort((a, b) => {
      const aTitle = a.title.toLowerCase().includes(lowerQuery);
      const bTitle = b.title.toLowerCase().includes(lowerQuery);
      if (aTitle && !bTitle) return -1;
      if (!aTitle && bTitle) return 1;
      return 0;
    });

    return filtered.slice(0, 10);
  }, []);

  // Update results when query or category changes
  useEffect(() => {
    const searchResults = performSearch(query, selectedCategory);
    setResults(searchResults);
    setHighlightedIndex(0);
  }, [query, selectedCategory, performSearch]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
    if (!isOpen) {
      setQuery('');
      setSelectedCategory('All');
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => Math.min(prev + 1, results.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[highlightedIndex]) {
            handleResultClick(results[highlightedIndex]);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, highlightedIndex, onClose]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (resultsRef.current) {
      const highlightedElement = resultsRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleResultClick = (item: SearchItem) => {
    navigate(item.href);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-900/60 dark:bg-black/70 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-x-4 top-[10%] md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-2xl z-50">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-100 dark:border-slate-700">
            <Search className="w-5 h-5 text-slate-400 dark:text-slate-500 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search pages, features, resources..."
              className="flex-1 text-lg text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 bg-transparent outline-none"
            />
            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Category Filters */}
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50">
            <div className="flex flex-wrap gap-2">
              {searchCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all ${
                    selectedCategory === category
                      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-600'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Results */}
          <div 
            ref={resultsRef}
            className="max-h-[400px] overflow-y-auto"
          >
            {results.length > 0 ? (
              <div className="py-2">
                {!query && (
                  <p className="px-4 py-2 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Popular Pages
                  </p>
                )}
                {results.map((item, index) => {
                  const Icon = categoryIcons[item.category] || FileText;
                  const colorClass = categoryColors[item.category] || 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleResultClick(item)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={`w-full flex items-start gap-4 px-4 py-3 text-left transition-colors ${
                        highlightedIndex === index ? 'bg-slate-50 dark:bg-slate-700/50' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${colorClass}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-slate-900 dark:text-white truncate">
                            {item.title}
                          </h4>
                          <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full flex-shrink-0">
                            {item.type}
                          </span>
                        </div>
                        <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-400 line-clamp-1">
                          {item.description}
                        </p>
                      </div>
                      <ArrowRight className={`w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0 mt-1 transition-opacity ${
                        highlightedIndex === index ? 'opacity-100' : 'opacity-0'
                      }`} />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="py-12 text-center">
                <Search className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-slate-400 font-medium">No results found</p>
                <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
                  Try a different search term or category
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50">
            <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-[10px] font-mono">↑</kbd>
                  <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-[10px] font-mono">↓</kbd>
                  <span className="ml-1">Navigate</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-[10px] font-mono">↵</kbd>
                  <span className="ml-1">Select</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-[10px] font-mono">esc</kbd>
                  <span className="ml-1">Close</span>
                </span>
              </div>
              <span>{results.length} result{results.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchModal;
